import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Fab,
  Grid,
  TextField,
  InputAdornment,
  Chip,
  CircularProgress,
  Alert,
  AppBar,
  Toolbar,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Add,
  Search,
  FilterList,
  LocalBar,
  MoreVert,
  Logout,
  Settings,
  ShoppingCart,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import { fetchBottles } from '../../store/slices/bottlesSlice';
import { logoutLocal } from '../../store/slices/authSlice';
import { BottleCard } from '../../components/bottles/BottleCard';
import { AppLayout } from '../../components/common/AppLayout';
import { Bottle, BottleType } from '../../../domain/entities';
import { ROUTES, BOTTLE_TYPES_LABELS } from '../../../core/config/constants';

export const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { bottles, isLoading, error, totalBottles } = useAppSelector((state) => state.bottles);
  const { user } = useAppSelector((state) => state.auth);

  const [searchText, setSearchText] = useState('');
  const [selectedType, setSelectedType] = useState<BottleType | ''>('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    console.log('🏠 HomePage - useEffect déclenché');
    console.log('🏠 HomePage - État actuel:', { 
      isAuthenticated: user ? 'OUI' : 'NON',
      searchText, 
      selectedType,
      bottles: bottles.length,
      isLoading,
      error 
    });
    console.log('🏠 HomePage - Chargement des bouteilles...');
    dispatch(fetchBottles({ search: searchText, type: selectedType || undefined }));
  }, [dispatch, searchText, selectedType]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  const handleTypeFilter = (type: BottleType | '') => {
    setSelectedType(type);
  };

  const handleBottleView = (bottle: Bottle) => {
    navigate(`${ROUTES.BOTTLES}/${bottle.id}`);
  };

  const handleBottleEdit = (bottle: Bottle) => {
    navigate(`${ROUTES.BOTTLES}/${bottle.id}/edit`);
  };

  const handleBottleArchive = (bottle: Bottle) => {
    // TODO: Implement archive functionality
    console.log('Archive bottle:', bottle);
  };

  const handleBottleDelete = (bottle: Bottle) => {
    // TODO: Implement delete functionality
    console.log('Delete bottle:', bottle);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    // Déconnexion locale immédiate et fiable
    dispatch(logoutLocal());
    handleMenuClose();
    // Redirection vers la page de connexion
    navigate(ROUTES.LOGIN, { replace: true });
  };

  const handleSettings = () => {
    navigate(ROUTES.SETTINGS);
    handleMenuClose();
  };

  const handleCartClick = () => {
    // Redirection vers la boutique Vinosk
    window.open('https://vinosk.fr/api/payment/shop/tag', '_blank');
  };

  const bottleTypes = Object.entries(BOTTLE_TYPES_LABELS) as [BottleType, string][];

  return (
    <AppLayout>
      <AppBar position="sticky" elevation={0}>
        <Toolbar>
          <Box
            component="img"
            src="https://vinosk.fr/images/logo-white.png"
            alt="Vinosk Logo"
            sx={{
              height: 32,
              width: 32,
              mr: 2,
            }}
          />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Ma Cave ({totalBottles})
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              size="large"
              color="inherit"
              onClick={handleCartClick}
              sx={{ mr: 1 }}
              title="Panier"
            >
              <ShoppingCart />
            </IconButton>
            <IconButton
              size="large"
              edge="end"
              color="inherit"
              onClick={handleMenuOpen}
            >
              <MoreVert />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ pt: 2, pb: 2 }}>
        {/* Search and Filters */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Rechercher une bouteille..."
            value={searchText}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Chip
              label="Tous"
              onClick={() => handleTypeFilter('')}
              color={selectedType === '' ? 'primary' : 'default'}
              variant={selectedType === '' ? 'filled' : 'outlined'}
            />
            {bottleTypes.map(([type, label]) => (
              <Chip
                key={type}
                label={label}
                onClick={() => handleTypeFilter(type)}
                color={selectedType === type ? 'primary' : 'default'}
                variant={selectedType === type ? 'filled' : 'outlined'}
              />
            ))}
          </Box>
        </Box>

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Loading */}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Empty State */}
        {!isLoading && bottles.length === 0 && (
          <Box
            sx={{
              textAlign: 'center',
              py: 8,
              color: 'text.secondary',
            }}
          >
            <LocalBar sx={{ fontSize: 80, mb: 2, opacity: 0.3 }} />
            <Typography variant="h5" gutterBottom>
              Aucune bouteille trouvée
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {searchText || selectedType
                ? 'Essayez de modifier vos critères de recherche'
                : 'Commencez par ajouter votre première bouteille'}
            </Typography>
          </Box>
        )}

        {/* Bottles Grid */}
        {!isLoading && bottles.length > 0 && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(4, 1fr)',
              },
              gap: 2,
            }}
          >
            {bottles.map((bottle) => (
              <Box key={bottle.id}>
                <BottleCard
                  bottle={bottle}
                  onView={handleBottleView}
                  onEdit={handleBottleEdit}
                  onArchive={handleBottleArchive}
                  onDelete={handleBottleDelete}
                />
              </Box>
            ))}
          </Box>
        )}
      </Container>

      {/* Floating Action Button */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 100,
          right: 16,
        }}
      >
        <Fab
          color="primary"
          onClick={() => navigate(ROUTES.BOTTLE_ADD)}
          sx={{ boxShadow: 3 }}
        >
          <Add />
        </Fab>
      </Box>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleSettings}>
          <Settings sx={{ mr: 1 }} />
          Paramètres
        </MenuItem>
        <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
          <Logout sx={{ mr: 1 }} />
          Déconnexion
        </MenuItem>
      </Menu>
    </AppLayout>
  );
};